import { useCallback, useEffect, useState } from 'react'
import { useManualQuery } from 'graphql-hooks'
import { useAppSelector } from '../store/hooks'
import { selectAuthUserSlice } from '../store/auth-user-slice'
import { getApproverTeamUserLocations } from '../graphql/custom-queries'
import * as logger from '../services/logger'
import { useLocationAdmin } from './use-location-admin'

interface IGetApproverTeamUserLocations {
  getUser: { approverToTeams?: { id: string; users?: { locationId?: string | null }[] | null }[] | null } | null
}

export interface ScopeLocationOptionsParams<T extends { id: string }> {
  locations: T[]
  isLocationAdmin: boolean
  managedLocationIds: string[]
  approverLocationIds: Set<string>
}

/** A location admin filters by their managed locations and, when also an approver, the locations of the users they approve. */
export function scopeLocationOptions<T extends { id: string }>({ locations, isLocationAdmin, managedLocationIds, approverLocationIds }: ScopeLocationOptionsParams<T>): T[] {
  if (!isLocationAdmin) return locations
  return locations.filter((location) => managedLocationIds.includes(location.id) || approverLocationIds.has(location.id))
}

/**
 * The locations a page may offer as filter options. Unlike filterLocations, which scopes what a location admin may edit,
 * this also covers the approver side of a location admin who approves departments.
 */
export const useLocationFilterScope = () => {
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const { isLocationAdmin, managedLocationIds } = useLocationAdmin()
  const [approverLocationIds, setApproverLocationIds] = useState<Set<string>>(new Set())
  const [getApproverTeamUserLocationsQuery] = useManualQuery<IGetApproverTeamUserLocations, { id: string }>(getApproverTeamUserLocations)

  const approverTeamsKey = (authUser.approverToTeams ?? [])
    .map((team) => team.id)
    .sort()
    .join('#')

  useEffect(() => {
    if (!isLocationAdmin || !approverTeamsKey) {
      setApproverLocationIds(new Set())
      return
    }
    let current = true
    getApproverTeamUserLocationsQuery({ variables: { id: authUser.id } })
      .then((response) => {
        if (!current) return
        const teams = response.data?.getUser?.approverToTeams ?? []
        setApproverLocationIds(new Set(teams.flatMap((team) => (team.users ?? []).flatMap((user) => (user.locationId ? [user.locationId] : [])))))
      })
      .catch((error) => logger.warning(error))
    return () => {
      current = false
    }
  }, [isLocationAdmin, approverTeamsKey, authUser.id])

  const scopeLocations = useCallback(
    <T extends { id: string }>(locations: T[]): T[] => scopeLocationOptions({ locations, isLocationAdmin, managedLocationIds, approverLocationIds }),
    [isLocationAdmin, managedLocationIds, approverLocationIds]
  )

  return { scopeLocations }
}
